import { useMutation } from '@apollo/client';
import { Box, Button, Container, Paper, TextField, Typography, styled } from '@mui/material';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ForgotPasswordDocument } from '~/generated/graphql';
import logo from '~/images/fc-logo.svg';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  backgroundColor: theme.palette.primary.main,
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0, 2),
  padding: theme.spacing(6),
  border: '1px solid #E6E6E6',
  borderRadius: '2px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const Logo = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 40%',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '75%',
  padding: theme.spacing(0, 0, 1),
}));

const messageSx = {
  textTransform: 'uppercase' as const,
  color: 'text.primary',
  px: 0,
  pt: 0,
  pb: 2,
};

const Form = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const FormInput = styled('div')(({ theme }) => ({
  margin: theme.spacing(1.5, 0),
}));

export const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');

  const [forgotPassword, { called, error, loading }] = useMutation(ForgotPasswordDocument, {
    fetchPolicy: 'no-cache',
  });

  return (
    <Root>
      <Helmet title={t('pages.auth.forgot_password.title')} />
      <Container maxWidth="sm">
        <FormPaper elevation={5}>
          <Logo>
            <img alt="FanConnect" src={logo} />
          </Logo>
          <Typography component="h1" variant="h5" gutterBottom sx={messageSx}>
            {t('pages.auth.forgot_password.title')}
          </Typography>
          {!called || loading ? (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {t('pages.auth.forgot_password.subtitle')}
              </Typography>
              <Form
                onSubmit={async (event) => {
                  event.preventDefault();
                  await forgotPassword({ variables: { email } });
                }}
              >
                <FormInput>
                  <TextField
                    type="email"
                    label={t('common.labels.email')}
                    autoComplete="email"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                  />
                </FormInput>
                <FormInput>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    color="secondary"
                    size="large"
                    disabled={!email || loading}
                  >
                    {t('pages.auth.forgot_password.reset_password')}
                  </Button>
                </FormInput>
              </Form>
            </>
          ) : error ? (
            <>
              <Typography textAlign="center" marginY="8px" color="error" role="alert">
                {t('pages.auth.forgot_password.error')}
              </Typography>
              <FormInput>
                <Button
                  variant="contained"
                  fullWidth
                  color="secondary"
                  size="large"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  {t('pages.auth.forgot_password.reload_page')}
                </Button>
              </FormInput>
            </>
          ) : (
            <Typography variant="subtitle1" textAlign="center" marginY="8px">
              {t('pages.auth.forgot_password.success', { email })}
            </Typography>
          )}
        </FormPaper>
        <Box flex="1 1 40%" />
      </Container>
    </Root>
  );
};
